<script setup lang="ts">

import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'
import EffortLeaderboardCard from '@/components/EffortLeaderboardCard.vue'
import type { EffortLeaderboardPlayer } from '@/types/leaderboard-player'
import { useAuth } from '@/composables/auth'
import { useEffortLeaderBoard } from '@/composables/game/leader-board'
import { T, useTranslate } from '@tolgee/vue'
import webapp from '@/services/webapp'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import LeaderboardLoyaltyModal from '@/components/modals/LeaderboardLoyaltyModal.vue'
import { useGuide } from '@/composables/game/guide'
import { UserGuide } from '@/types/onboarding'
import CupImage from '@/assets/images/gifs/cup.gif'
import UserGuideModal from '@/components/modals/UserGuideModal.vue'

const { profile } = useAuth()

const { getEffortLeaderboard } = useEffortLeaderBoard()
const { shouldShowGuide, removeGuide } = useGuide()
const { t } = useTranslate()

const loading = ref(false)

const isPlayerOutOfBoard = ref(false)
const inScrollView = ref(false)
const showLeaderboardLoyaltyModal = ref(false)
const showGuideModal = ref(false)

const container = ref<HTMLElement | null>(null)

const playerForCard = ref<EffortLeaderboardPlayer>({
  firstName: profile.value.firstName,
  photoUrl: profile.value.photoUrl,
  score: 0,
  isThisPlayer: true,
  isPremium: webapp.isPremium,
})

const maxVisibleRank = ref<number>(100)
const listLimit = ref<number>(100)

const players = ref<EffortLeaderboardPlayer[]>([])
const currentPlayerIndex = ref<number | null>(null)

const fullScreenStyles = computed(() => {
  return { bottom: `${webapp.safeAreaInset.bottom + 70 }px` }
})
function handleScroll() {
  if (container.value && currentPlayerIndex.value) {
    const cardElement = container.value.querySelector(`.card:nth-child(${currentPlayerIndex.value})`) as HTMLElement
    if (cardElement) {
      const cardRect = cardElement.getBoundingClientRect()
      inScrollView.value = cardRect.bottom <= window.innerHeight - 90
    }
  }
}

onBeforeMount(() => {
  showGuideModal.value = shouldShowGuide(UserGuide.LoyaltyLeaderboard)
})

onMounted(async () => {
  loading.value = true
  try {
    const {
      players: playerList,
      thisPlayer,
    } = await getEffortLeaderboard()
    currentPlayerIndex.value = thisPlayer.rank
    isPlayerOutOfBoard.value = thisPlayer.rank > playerList.length
    playerForCard.value.score = thisPlayer.score
    players.value = playerList
    loading.value = false
  } catch {
    loading.value = false
  }
})

watch([container, currentPlayerIndex], () => {
  nextTick(() => {
    handleScroll()
  })
}, { immediate: true })

onMounted(() => {
  document.getElementsByClassName('scroll-container')[0]?.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  document.getElementsByClassName('scroll-container')[0]?.removeEventListener('scroll', handleScroll)
})

</script>

<template>
  <div
    ref="container"
    :class="twMerge('pt-9 flex flex-col px-4 items-center relative w-full', !players.length && 'h-full flex-1')"
  >
    <InfoCircleIcon
      class="w-6 h-6 min-w-[24px] text-white/70 top-6 right-6 absolute cursor-pointer"
      @click="showLeaderboardLoyaltyModal = true"
    />
    <img
      src="@/assets/images/gifs/duck-winner.gif"
      class=" w-[120px] h-[120px] min-w-[120px] mb-6 bounce-down-fast"
    >
    <h1
      class="text-light-white text-[32px] leading-8 font-bold uppercase bounce-in-right mb-10"
    >
      <T
        key-name="leaderboard.loyalty.title"
        default-value="Loyalty Ranking"
      />
    </h1>
    <div
      v-if="!loading"
      class="w-full h-full flex flex-col"
    >
      <div
        v-if="players.length"
        :class="twMerge('flex flex-col w-full gap-y-3 pb-4 items-center')"
      >
        <EffortLeaderboardCard
          v-for="(leadingBoardPlayer, index) in players.slice(0, maxVisibleRank)"
          :key="index"
          :player="leadingBoardPlayer"
          :index="(index+1).toString()"
          :is-own="index+1 === currentPlayerIndex"
          class="card"
        />
        <p
          v-if="isPlayerOutOfBoard || listLimit <= players.length"
          class="mt-3 mb-6 text-center max-w-[314px] text-base font-medium text-white/60"
        >
          <T
            key-name="levels.many_more"
            default-value="And many more players who didn’t make it into the top {limit}... yet."
            :params="{limit: Math.min(players.length, listLimit)}"
          />
        </p>
        <EffortLeaderboardCard
          v-if="isPlayerOutOfBoard"
          :player="playerForCard"
          :index="`${(maxVisibleRank).toString()}+`"
          card-class="invisible"
        />
      </div>
      <div
        v-else
        class="flex flex-1 h-full flex-col items-center justify-center gap-y-3 pb-12 w-full text-center"
      >
        <h2 class="text-[32px] leading-[35px] -tracking-[0.64px] font-bold text-light-white">
          <T
            key-name="levels.empty.title"
            default-value="Nothing to see here yet"
          />
        </h2>
        <p class="text-base leading-[17.6px] font-medium tracking-tight text-white">
          <T
            key-name="levels.empty.description"
            default-value="Once users reach this level you will see a list here"
          />
        </p>
      </div>
      <div
        v-if="isPlayerOutOfBoard || !inScrollView"
        class="w-full fixed pr-8 z-20"
        :style="fullScreenStyles"
      >
        <EffortLeaderboardCard
          :player="playerForCard"
          :index="currentPlayerIndex === null ? `${(maxVisibleRank).toString()}+` : currentPlayerIndex?.toString() ?? ''"
          is-own
          card-class="border-green-primary"
        />
      </div>
    </div>
  </div>

  <LeaderboardLoyaltyModal
    v-if="showLeaderboardLoyaltyModal"
    :show="showLeaderboardLoyaltyModal"
    centered
    @hide="showLeaderboardLoyaltyModal = false"
  />

  <UserGuideModal
    v-if="showGuideModal"
    :show="showGuideModal"
    :image="CupImage"
    :title="t('guide.loyalty_title', 'Climb our Loyalty Ranking!')"
    :description="t('guide.loyalty_description', `Join the race to the top by being one of the most active players. <br/><br/> Earn effort points through daily activities and interactions— the more you engage, the higher you rise!`)"
    @hide="showGuideModal = false; removeGuide(UserGuide.LoyaltyLeaderboard)"
  />
</template>
